import path from "path";

import express from "express";
import { get, identity } from "lodash";
import { BodyOptions } from "routing-controllers";

import {
  BenchmarkThresholdMax,
  BenchmarkThresholdMid,
  BenchmarkThresholdMin,
  DataIngestionTypeBenchmarks,
  DataIngestionTypeCarta,
  DataIngestionTypeEmployeeMetadata,
  DataIngestionTypeFinchFallbackUser,
  ExcelRowExtractionOptions,
  HttpDelete,
  HttpGet,
  HttpPatch,
  HttpPost,
  HttpPut,
  LocalCurrency,
  RetoolLogTypeError,
  RetoolLogTypeInfo,
  RetoolLogTypeWarning,
  SalaryIncomeFrequencyMap,
  TokenTypeInvitation,
  TokenTypePasswordReset,
  USDollar,
} from "@/utils/types";
import { SalaryIncomeFrequency } from "prisma/cm/client";

export const ASSET_ROOT = get(
  process.env,
  "ASSET_ROOT",
  path.join(process.cwd(), "lib/api/cm/assets"),
);

/*
 * Requires:
 * - at least one lower-case letter
 * - at least one upper-case letter
 * - at least one number
 * - at least one special character (including whitespace): !"#$%&'()*+,\-./:;<=>?@\[\]^\\_`{|}~
 * - between 10 and 128 characters
 */
export const PASSWORD_REGEX =
  /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[ !"#$%&'()*+,\-./:;<=>?@[\]^\\_`{|}~])[a-zA-Z0-9 !"#$%&'()*+,\-./:;<=>?@[\]^\\_`{|}~]{10,128}$/;

export const DEFAULT_SERIALIZER_OPTIONS: BodyOptions = {
  required: true,
  validate: { whitelist: true },
};

export const DATA_INGESTION_TYPE_BENCHMARKS: DataIngestionTypeBenchmarks = "benchmarks";
export const DATA_INGESTION_TYPE_CARTA: DataIngestionTypeCarta = "carta";
export const DATA_INGESTION_TYPE_EMPLOYEE_METADATA: DataIngestionTypeEmployeeMetadata = "metadata";
export const DATA_INGESTION_TYPE_FINCH_FALLBACK_EMPLOYMENT_DATA: DataIngestionTypeFinchFallbackUser =
  "employee";

export const NODE_ENV_MAP = {
  development: "development",
  production: "production",
} as const;

export const DEFAULT_COOKIE_SETTINGS: express.CookieOptions = {
  secure: process.env.NODE_ENV !== NODE_ENV_MAP.development,
  domain: process.env.CSRF_DOMAIN || "comprehensive.io",
  sameSite: process.env.NODE_ENV !== NODE_ENV_MAP.development ? "none" : "lax",
};

export const PASSWORD_TOKEN_EXPIRATION_IN_SECONDS = 60 * 60; // ONE HOUR
export const INVITATION_TOKEN_EXPIRATION_IN_SECONDS = 60 * 60 * 24 * 7; // SEVEN DAYS

export const SALARY_INCOME_FREQUENCY_MAP: SalaryIncomeFrequencyMap = {
  [SalaryIncomeFrequency.HOURLY]: "Hourly",
  [SalaryIncomeFrequency.DAILY]: "Daily",
  [SalaryIncomeFrequency.WEEKLY]: "Weekly",
  [SalaryIncomeFrequency.BI_WEEKLY]: "Bi-weekly",
  [SalaryIncomeFrequency.SEMI_MONTHLY]: "Semi-monthly",
  [SalaryIncomeFrequency.MONTHLY]: "Monthly",
  [SalaryIncomeFrequency.YEARLY]: "Yearly",
  [SalaryIncomeFrequency.FIXED]: "Fixed",
};
export const SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST: SalaryIncomeFrequency[] = [
  SalaryIncomeFrequency.HOURLY,
  SalaryIncomeFrequency.DAILY,
];

export const TOKEN_TYPE_PASSWORD_RESET: TokenTypePasswordReset = "PASSWORD_RESET";
export const TOKEN_TYPE_INVITATION: TokenTypeInvitation = "INVITATION";

export const BENCHMARK_THRESHOLD_MIN: BenchmarkThresholdMin = "MIN";
export const BENCHMARK_THRESHOLD_MID: BenchmarkThresholdMid = "MID";
export const BENCHMARK_THRESHOLD_MAX: BenchmarkThresholdMax = "MAX";

export const RETOOL_LOG_TYPE_ERROR: RetoolLogTypeError = "ERROR";
export const RETOOL_LOG_TYPE_WARNING: RetoolLogTypeWarning = "WARNING";
export const RETOOL_LOG_TYPE_INFO: RetoolLogTypeInfo = "INFO";

export const HTTP_GET: HttpGet = "GET";
export const HTTP_POST: HttpPost = "POST";
export const HTTP_PUT: HttpPut = "PUT";
export const HTTP_PATCH: HttpPatch = "PATCH";
export const HTTP_DELETE: HttpDelete = "DELETE";

export const DEFAULT_ROW_EXTRACTION_OPTIONS: ExcelRowExtractionOptions = {
  requiredColumns: [],
  allowExtraColumns: false,
  skip: 0,
  headerPostProcessor: identity,
};

export const US_DOLLAR: USDollar = "USD";
export const LOCAL_CURRENCY: LocalCurrency = "LOCAL_CURRENCY";

export const MIME_EXCEL = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const MIME_PDF = "application/pdf";
